/**
 * Move list content (ARCHITECTURE §5 Controls + VISION §4). Keys are logical buttons (labelled per device by
 * `InputManager.promptFor`) or literal glyphs ("×2", "→", "+"), for a head facing RIGHT (R = forward, L = back).
 * The fakes match HOW TO PLAY's table (ui/tutorial/fakes.ts; fakes.test.ts checks names, FAKE tags and costs).
 */
import { MatchFormat, type ButtonName } from '@hf/shared';

export type MoveKey = ButtonName | '+' | '→' | '×2' | 'HOLD' | 'AIR' | 'DASH' | 'LET GO';
export interface Move {
  name: string;
  keys: readonly MoveKey[];
  note: string;
  /** Feints get a pink FAKE tag. */
  fake?: boolean;
}
export interface MoveSection {
  title: string;
  tone: 'gold' | 'red' | 'blue' | 'pink';
  moves: readonly Move[];
}

export const MOVE_SECTIONS: readonly MoveSection[] = [
  {
    title: 'BEFORE THE SNAP',
    tone: 'gold',
    moves: [
      { name: 'SNAP', keys: ['A'], note: 'Start the play. The defender must wait for GO!' },
      {
        name: 'FIELD GOAL',
        keys: ['HOLD', 'D', '+', 'A'],
        note: 'Snap in toss formation: throw it through the uprights',
      },
      {
        name: 'HARD COUNT',
        keys: ['U'],
        note: 'Fake snap call. Free, max 2 per play. Draws offside',
        fake: true,
      },
    ],
  },
  {
    title: 'QUARTERBACK',
    tone: 'red',
    moves: [
      {
        name: 'THROW',
        keys: ['HOLD', 'A'],
        note: 'Hold to charge the arc, release to throw — then chase it',
      },
      {
        name: 'PUMP FAKE',
        keys: ['HOLD', 'A', '→', 'D'],
        note: 'Cancel the throw. Max 1 per play',
        fake: true,
      },
      { name: 'DASH', keys: ['R', '×2'], note: 'Double-tap a direction (back works too)' },
      {
        name: 'STUTTER',
        keys: ['DASH', '→', 'L'],
        note: 'Dash at him, tap back at once. Baits the dive',
        fake: true,
      },
    ],
  },
  {
    title: 'BALL CARRIER',
    tone: 'red',
    moves: [
      { name: 'HURDLE', keys: ['U'], note: 'Beats a DIVE. Loses to a LEAP' },
      { name: 'TRUCK', keys: ['HOLD', 'D'], note: 'Stay low and charge. Beats a LEAP. Loses to a DIVE' },
      {
        name: 'FAKE JUMP',
        keys: ['U', '→', 'D'],
        note: 'Slam-cancel while rising, then truck under the leap',
        fake: true,
      },
      { name: 'STIFF-ARM', keys: ['A'], note: 'Fist forward. Beats a standing grab' },
      {
        name: 'SPIN',
        keys: ['D', '+', 'A'],
        note: 'Tackle-proof dodge. Long recovery if it whiffs',
        fake: true,
      },
      {
        name: 'HESITATION',
        keys: ['LET GO', '→', 'R'],
        note: 'Stop dead for a moment, then burst. Free',
        fake: true,
      },
    ],
  },
  {
    title: 'DEFENSE',
    tone: 'blue',
    moves: [
      { name: 'DIVE', keys: ['A'], note: 'Low tackle. Beats a TRUCK. Loses to a HURDLE' },
      {
        name: 'LEAP',
        keys: ['U', '→', 'A'],
        note: 'High tackle in the air. Beats a HURDLE. Loses to a TRUCK',
      },
      {
        name: 'FAKE DIVE',
        keys: ['A', '→', 'D'],
        note: 'Cancel the dive wind-up to bait the hurdle',
        fake: true,
      },
      {
        name: 'FAST-FALL',
        keys: ['AIR', 'D'],
        note: 'In the air: slam down to bait the catch. Free',
        fake: true,
      },
      { name: 'CATCH / SWAT', keys: ['A'], note: 'Ball in the air: grab it for a pick or knock it down' },
    ],
  },
];

/** Classic format only: the kick plays (kickoff / onside / punt tosses). */
export const SPECIAL_TEAMS_SECTION: MoveSection = {
  title: 'SPECIAL TEAMS · CLASSIC',
  tone: 'gold',
  moves: [
    { name: 'KICKOFF', keys: ['HOLD', 'A'], note: 'Hold for power, release to kick it deep' },
    {
      name: 'ONSIDE KICK',
      keys: ['HOLD', 'A', '+', 'D'],
      note: 'Hold ▼ at the release: a short hop you can recover past the 10',
    },
    { name: 'PUNT', keys: ['HOLD', 'S', '+', 'A'], note: 'At the snap, any down: boot it away' },
    {
      name: 'TAKE A KNEE',
      keys: ['HOLD', 'D'],
      note: 'Returner with the ball in your own end zone: touchback (25 on a kickoff, 20 on a punt)',
    },
  ],
};

/** The move list of a match format (Classic adds the special-teams plays). */
export const moveSectionsFor = (format: number | undefined): readonly MoveSection[] =>
  format === MatchFormat.CLASSIC
    ? [MOVE_SECTIONS[0]!, SPECIAL_TEAMS_SECTION, ...MOVE_SECTIONS.slice(1)]
    : MOVE_SECTIONS;

/** The counter square: rows = carrier, columns = defender. */
export const COUNTER_SQUARE = {
  cols: ['DIVE', 'LEAP'],
  rows: [
    { name: 'HURDLE', cells: ['carrier', 'defender'] as const },
    { name: 'TRUCK', cells: ['defender', 'carrier'] as const },
  ],
} as const;
